import React, { useState, useMemo, useCallback } from 'react';
// @ts-ignore
import html2pdf from 'html2pdf.js';
import type { Department, StaffMember, Shift, Schedule, View, AdminUser, AssignmentStatus } from '../../types';
import ScheduleCell from './ScheduleCell';
import ShiftPalette from './ShiftPalette';
import RepeatScheduleModal from './RepeatScheduleModal';
import Modal from '../ui/Modal';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../layout/LoadingSpinner';
import { Icons } from '../ui/Icons';
import { getWeekStartDate, getWeekDates, formatDateYYYYMMDD, formatDayAndDate, formatDateRange } from '../../utils/dateUtils';
import { PrintData } from './PrintPage';
import { DAY_INDEX_MAP, DayOfWeek, WeeklyMetrics } from '../../types';

import StaffProfileModal from '../staff/StaffProfileModal';
import { CSVImportModal } from './CSVImportModal';

type SchedulablePerson = (StaffMember & { uid?: never }) | (Omit<AdminUser, 'uid'> & { id: string });

interface SchedulerViewProps {
    departments: Department[];
    people: SchedulablePerson[];
    shifts: Shift[];
    schedule: Schedule;
    weeklyMetrics: WeeklyMetrics;
    weekStartDay: DayOfWeek;
    setSchedule: React.Dispatch<React.SetStateAction<Schedule>>;
    setWeeklyMetrics: React.Dispatch<React.SetStateAction<WeeklyMetrics>>;
    setView: (view: View) => void;
    onScheduleGenerated: (weekStartDate: Date) => void;
    onCellUpdate: (staffId: string, date: string, shiftId: string | null, status: AssignmentStatus, isForced?: boolean) => void;
    onRepeatSchedule: (sourceWeekStartDate: Date, repeatStartDate: Date, repeatEndDate: Date) => void;
    onInitiatePrint: (data: PrintData) => void;
    onInitiatePDFPreview?: (data: PrintData) => void;
}

const dayHeaderStyles = [
    'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300',       // Monday
    'bg-orange-50 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300', // Tuesday
    'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300',   // Wednesday
    'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300',   // Thursday
    'bg-teal-50 text-teal-800 dark:bg-teal-900/20 dark:text-teal-300',     // Friday
    'bg-blue-50 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',     // Saturday
    'bg-indigo-50 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300', // Sunday
];

const dayHeaderBorderStyles = [
    'border-t-4 border-red-200 dark:border-red-700',
    'border-t-4 border-orange-200 dark:border-orange-700',
    'border-t-4 border-amber-200 dark:border-amber-700',
    'border-t-4 border-green-200 dark:border-green-700',
    'border-t-4 border-teal-200 dark:border-teal-700',
    'border-t-4 border-blue-200 dark:border-blue-700',
    'border-t-4 border-indigo-200 dark:border-indigo-700',
];

const WeekNavigator: React.FC<{
    currentWeekStartDate: Date;
    onPrevious: () => void;
    onNext: () => void;
    onToday: () => void;
    onRepeat: () => void;
    onPrint: () => void;
    onDownloadPDF: () => void;
    onImportCSV: () => void;
    totalWeeklyHours: number;
    isDownloadingPDF: boolean;
}> = ({ currentWeekStartDate, onPrevious, onNext, onToday, onRepeat, onPrint, onDownloadPDF, onImportCSV, totalWeeklyHours, isDownloadingPDF }) => (
    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 p-4 rounded-xl shadow-sm">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
             <div className="flex items-center gap-1 bg-white dark:bg-slate-700/50 p-1 rounded-lg border border-gray-200 dark:border-slate-700">
                <button onClick={onPrevious} className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-600 dark:text-gray-350"><Icons.ChevronLeft className="h-5 w-5" /></button>
                <button onClick={onNext} className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-600 dark:text-gray-350"><Icons.ChevronRight className="h-5 w-5" /></button>
            </div>
            <button onClick={onToday} className="px-3 py-2 text-sm font-medium border bg-white dark:bg-slate-700/50 border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200">Today</button>
            <button onClick={onRepeat} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border bg-white dark:bg-slate-700/50 border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200">
                <Icons.Copy className="w-4 h-4" />
                <span className="hidden xs:inline">Repeat</span>
            </button>
            <button onClick={onPrint} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border bg-white dark:bg-slate-700/50 border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200">
                <Icons.Printer className="w-4 h-4" />
                <span className="hidden xs:inline">Print</span>
            </button>
            <button 
                onClick={onDownloadPDF} 
                disabled={isDownloadingPDF}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border border-indigo-200 dark:border-indigo-850 bg-indigo-50 hover:bg-indigo-100/80 dark:bg-indigo-905/40 dark:hover:bg-indigo-905/60 rounded-lg text-indigo-700 dark:text-indigo-350 shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {isDownloadingPDF ? (
                    <Icons.Loader className="w-4 h-4 animate-spin" />
                ) : (
                    <Icons.Download className="w-4 h-4" />
                )}
                <span>{isDownloadingPDF ? 'Downloading...' : 'Download PDF'}</span>
            </button>
            <button onClick={onImportCSV} className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border border-teal-200 dark:border-teal-850 bg-teal-50 hover:bg-teal-100/80 dark:bg-teal-900/40 dark:hover:bg-teal-900/60 rounded-lg text-teal-700 dark:text-teal-350 shadow-sm transition">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                <span className="hidden xs:inline">Import CSV</span>
            </button>
        </div>
        <div className="flex items-center gap-4 text-right">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200">
                {formatDateRange(currentWeekStartDate)}
            </h2>
            <div className="w-px h-10 bg-gray-200 dark:bg-slate-700"></div>
            <div className="p-2 rounded-lg bg-white/50 dark:bg-slate-700/50">
                <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider">Total Hours</p>
                <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                    {totalWeeklyHours.toFixed(1)}
                </p>
            </div>
        </div>
    </div>
);

function oklchToRgba(cssText: string): string {
    return cssText.replace(/oklch\(\s*([\d.]+%?)(?:\s+|,\s*)([\d.]+%?)(?:\s+|,\s*)([\d.-]+(?:deg|rad|grad|turn)?%?)(?:\s*(?:\/|,)\s*([\d.]+%?))?\s*\)/gi, (match, p1, p2, p3, p4) => {
        try {
            const L = p1.endsWith('%') ? parseFloat(p1) / 100 : parseFloat(p1);
            const C = p2.endsWith('%') ? (parseFloat(p2) / 100) * 0.4 : parseFloat(p2);
            
            let H = 0;
            const h_str = p3;
            if (h_str.endsWith('deg')) {
                H = parseFloat(h_str.slice(0, -3));
            } else if (h_str.endsWith('rad')) {
                H = (parseFloat(h_str.slice(0, -3)) * 180) / Math.PI;
            } else if (h_str.endsWith('turn')) {
                H = parseFloat(h_str.slice(0, -4)) * 360;
            } else if (h_str.endsWith('grad')) {
                H = parseFloat(h_str.slice(0, -4)) * 0.9;
            } else if (h_str.endsWith('%')) {
                H = (parseFloat(h_str) / 100) * 360;
            } else {
                H = parseFloat(h_str);
            }

            const alpha = p4 ? (p4.endsWith('%') ? parseFloat(p4) / 100 : parseFloat(p4)) : 1;

            const hRad = (H * Math.PI) / 180;
            const ok_a = C * Math.cos(hRad);
            const ok_b = C * Math.sin(hRad);

            // Convert Oklab to LMS
            const l_ = L + 0.3963377774 * ok_a + 0.2158037573 * ok_b;
            const m_ = L - 0.1055613458 * ok_a - 0.0638541728 * ok_b;
            const s_ = L - 0.0894841775 * ok_a - 1.291485548 * ok_b;

            // Reconstruct non-linear LMS
            const lm = l_ * l_ * l_;
            const mm = m_ * m_ * m_;
            const sm = s_ * s_ * s_;

            // LMS to Linear sRGB
            const rLinear = +4.0767416621 * lm - 3.3077115913 * mm + 0.2309699292 * sm;
            const gLinear = -1.2684380046 * lm + 2.6097574011 * mm - 0.3413193965 * sm;
            const bLinear = -0.0041960063 * lm - 0.7034186147 * mm + 1.707614701 * sm;

            // Linear sRGB to standard sRGB
            const toSRGB = (c: number) => {
                return c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
            };

            const r = Math.round(Math.max(0, Math.min(1, toSRGB(rLinear))) * 255);
            const g = Math.round(Math.max(0, Math.min(1, toSRGB(gLinear))) * 255);
            const b = Math.round(Math.max(0, Math.min(1, toSRGB(bLinear))) * 255);

            if (alpha !== 1) {
                return `rgba(${r}, ${g}, ${b}, ${alpha})`;
            }
            return `rgb(${r}, ${g}, ${b})`;
        } catch (err) {
            console.error('Failed to convert oklch:', match, err);
            return '#cccccc';
        }
    });
}

function oklabToRgba(cssText: string): string {
    return cssText.replace(/oklab\(\s*([\d.]+%?)(?:\s+|,\s*)([\d.-]+%?)(?:\s+|,\s*)([\d.-]+%?)(?:\s*(?:\/|,)\s*([\d.]+%?))?\s*\)/gi, (match, p1, p2, p3, p4) => {
        try {
            const L = p1.endsWith('%') ? parseFloat(p1) / 100 : parseFloat(p1);
            const ok_a = p2.endsWith('%') ? (parseFloat(p2) / 100) * 0.4 : parseFloat(p2);
            const ok_b = p3.endsWith('%') ? (parseFloat(p3) / 100) * 0.4 : parseFloat(p3);
            const alpha = p4 ? (p4.endsWith('%') ? parseFloat(p4) / 100 : parseFloat(p4)) : 1;

            // Convert Oklab to LMS
            const l_ = L + 0.3963377774 * ok_a + 0.2158037573 * ok_b;
            const m_ = L - 0.1055613458 * ok_a - 0.0638541728 * ok_b;
            const s_ = L - 0.0894841775 * ok_a - 1.291485548 * ok_b;

            // Reconstruct non-linear LMS
            const lm = l_ * l_ * l_;
            const mm = m_ * m_ * m_;
            const sm = s_ * s_ * s_;

            // LMS to Linear sRGB
            const rLinear = +4.0767416621 * lm - 3.3077115913 * mm + 0.2309699292 * sm;
            const gLinear = -1.2684380046 * lm + 2.6097574011 * mm - 0.3413193965 * sm;
            const bLinear = -0.0041960063 * lm - 0.7034186147 * mm + 1.707614701 * sm;

            // Linear sRGB to standard sRGB
            const toSRGB = (c: number) => {
                return c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
            };

            const r = Math.round(Math.max(0, Math.min(1, toSRGB(rLinear))) * 255);
            const g = Math.round(Math.max(0, Math.min(1, toSRGB(gLinear))) * 255);
            const b = Math.round(Math.max(0, Math.min(1, toSRGB(bLinear))) * 255);

            if (alpha !== 1) {
                return `rgba(${r}, ${g}, ${b}, ${alpha})`;
            }
            return `rgb(${r}, ${g}, ${b})`;
        } catch (err) {
            console.error('Failed to convert oklab:', match, err);
            return '#cccccc';
        }
    });
}

function sanitizeStylesheetsForPDF(): () => void {
    const originalStates: { sheet: CSSStyleSheet; disabled: boolean }[] = [];
    const tempStyleElements: HTMLStyleElement[] = [];
    const detachedStyles: { node: Element; parent: Node; nextSibling: Node | null }[] = [];

    try {
        const sheets = Array.from(document.styleSheets) as CSSStyleSheet[];
        for (const sheet of sheets) {
            try {
                // Save original state
                originalStates.push({ sheet, disabled: sheet.disabled });

                let rules: CSSRule[] = [];
                try {
                    rules = Array.from(sheet.cssRules || sheet.rules || []);
                } catch (ruleError) {
                    continue;
                }

                if (rules.length === 0) continue;

                let cssText = '';
                for (const rule of rules) {
                    cssText += rule.cssText + '\n';
                }

                const lowerCss = cssText.toLowerCase();
                if (lowerCss.includes('oklch') || lowerCss.includes('oklab')) {
                    let sanitizedCss = oklchToRgba(cssText);
                    sanitizedCss = oklabToRgba(sanitizedCss);
                    // Catch-all regex to strip any remaining modern colors as absolute backup
                    sanitizedCss = sanitizedCss.replace(/oklab\([^)]*\)/gi, 'rgb(0,0,0)');
                    sanitizedCss = sanitizedCss.replace(/oklch\([^)]*\)/gi, 'rgb(0,0,0)');

                    // Disable the original stylesheet
                    sheet.disabled = true;

                    // Dynamically detach the style node from head or parent.
                    // This physically removes it from document.styleSheets so html2canvas doesn't process it.
                    const owner = sheet.ownerNode;
                    if (owner && owner instanceof Element) {
                        const parent = owner.parentNode;
                        if (parent) {
                            detachedStyles.push({
                                node: owner,
                                parent: parent,
                                nextSibling: owner.nextSibling
                            });
                            parent.removeChild(owner);
                        }
                    }

                    // Create clean style block
                    const tempStyle = document.createElement('style');
                    tempStyle.type = 'text/css';
                    tempStyle.setAttribute('data-pdf-safe-style', 'true');
                    tempStyle.appendChild(document.createTextNode(sanitizedCss));
                    document.head.appendChild(tempStyle);

                    tempStyleElements.push(tempStyle);
                }
            } catch (err) {
                console.error('Error in sanitizeStylesheetsForPDF:', err);
            }
        }
    } catch (gErr) {
        console.error('Failed to iterate over stylesheets:', gErr);
    }

    // Return the restore callback
    return () => {
        // Restore original stylesheet states
        for (const state of originalStates) {
            try {
                state.sheet.disabled = state.disabled;
            } catch (e) {
                console.error('Failed to restore stylesheet disabled state:', e);
            }
        }

        // Re-attach detached original style nodes
        for (const detached of detachedStyles) {
            try {
                detached.parent.insertBefore(detached.node, detached.nextSibling);
            } catch (e) {
                console.error('Failed to re-attach stylesheet node:', e);
            }
        }

        // Remove temporary safe stylesheets
        for (const el of tempStyleElements) {
            try {
                el.remove();
            } catch (e) {
                console.error('Failed to remove temporary style element:', e);
            }
        }
    };
}

interface StylesBackup {
    element: HTMLElement;
    originalInlineStyles: { [key: string]: string };
}

function applyRGBInlineStyles(container: HTMLElement): () => void {
    const backupList: StylesBackup[] = [];
    const colorProps = [
        'color',
        'background-color',
        'border-color',
        'border-top-color',
        'border-bottom-color',
        'border-left-color',
        'border-right-color',
        'fill',
        'stroke'
    ];

    function traverse(node: Element) {
        if (node && (node as any).style && typeof (node as any).style.setProperty === 'function') {
            const htmlNode = node as HTMLElement;
            try {
                const win = htmlNode.ownerDocument?.defaultView || window;
                const computed = win.getComputedStyle ? win.getComputedStyle(htmlNode) : window.getComputedStyle(htmlNode);
                const backup: { [key: string]: string } = {};
                let hasChange = false;

                for (const prop of colorProps) {
                    try {
                        const val = computed.getPropertyValue(prop);
                        if (typeof val === 'string' && (val.includes('oklch') || val.includes('oklab'))) {
                            // Back up original inline style value
                            backup[prop] = htmlNode.style.getPropertyValue(prop);
                            
                            // Convert color functions found in computed styles
                            let converted = oklabToRgba(oklchToRgba(val));
                            if (converted.includes('oklch') || converted.includes('oklab')) {
                                converted = converted.replace(/oklab\([^)]*\)/gi, 'rgb(0,0,0)');
                                converted = converted.replace(/oklch\([^)]*\)/gi, 'rgb(0,0,0)');
                            }

                            // Apply standard RGB/RGBA or hex value as inline style (no computed priority is needed)
                            htmlNode.style.setProperty(prop, converted);
                            hasChange = true;
                        }
                    } catch (innerErr) {
                        // Skip any individual failed properties
                    }
                }

                if (hasChange) {
                    backupList.push({ element: htmlNode, originalInlineStyles: backup });
                }
            } catch (err) {
                // Ignore errors finding computed style for specific elements
            }
        }

        const children = node.children;
        if (children) {
            for (let i = 0; i < children.length; i++) {
                traverse(children[i]);
            }
        }
    }

    traverse(container);

    return () => {
        // Restore step
        for (const backup of backupList) {
            for (const [prop, originalVal] of Object.entries(backup.originalInlineStyles)) {
                try {
                    if (originalVal === '') {
                        backup.element.style.removeProperty(prop);
                    } else {
                        backup.element.style.setProperty(prop, originalVal);
                    }
                } catch (e) {
                    try {
                        (backup.element.style as any)[prop] = originalVal;
                    } catch (err) {
                        // Ignore restoration edge cases
                    }
                }
            }
        }
    };
}

const SchedulerView: React.FC<SchedulerViewProps> = ({ departments, people, shifts, schedule, weeklyMetrics, weekStartDay, setSchedule, setWeeklyMetrics, setView, onScheduleGenerated, onCellUpdate, onRepeatSchedule, onInitiatePrint, onInitiatePDFPreview }) => {
    const [draggedShiftId, setDraggedShiftId] = useState<string | null>(null);
    const [isRepeatModalOpen, setIsRepeatModalOpen] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
    const startDayIndex = DAY_INDEX_MAP[weekStartDay];
    const [currentWeekStartDate, setCurrentWeekStartDate] = useState(() => getWeekStartDate(new Date(), startDayIndex));

    // Update week start when settings change
    React.useEffect(() => {
        setCurrentWeekStartDate(getWeekStartDate(new Date(), startDayIndex));
    }, [startDayIndex]);
    const [isPaletteCollapsed, setIsPaletteCollapsed] = useState(false);
    const [selectedStaffForProfile, setSelectedStaffForProfile] = useState<SchedulablePerson | null>(null);

    // Report/PDF/Print Department Filter states
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [filterActionType, setFilterActionType] = useState<'print' | 'pdf' | null>(null);
    const [selectedDeptIds, setSelectedDeptIds] = useState<string[]>([]);
    const [isPendingPDFGeneration, setIsPendingPDFGeneration] = useState(false);
    const [pdfFilterDeptIds, setPdfFilterDeptIds] = useState<string[] | null>(null);
    const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
    const [pdfFilename, setPdfFilename] = useState<string>('');

    const handleClosePDFModal = () => {
        if (pdfBlobUrl) {
            URL.revokeObjectURL(pdfBlobUrl);
        }
        setPdfBlobUrl(null);
        setPdfFilename('');
    };

    // Initialize selectedDeptIds to all department IDs once departments are loaded
    React.useEffect(() => {
        if (departments.length > 0 && selectedDeptIds.length === 0) {
            setSelectedDeptIds(departments.map(d => d.id));
        }
    }, [departments]);

    const activePdfDepts = useMemo(() => {
        if (!pdfFilterDeptIds) return departments;
        return departments.filter(d => pdfFilterDeptIds.includes(d.id));
    }, [departments, pdfFilterDeptIds]);

    const weekDates = useMemo(() => getWeekDates(currentWeekStartDate), [currentWeekStartDate]);

    const goToPreviousWeek = () => setCurrentWeekStartDate(prev => {
        const newDate = new Date(prev);
        newDate.setDate(newDate.getDate() - 7);
        return newDate;
    });

    const goToNextWeek = () => setCurrentWeekStartDate(prev => {
        const newDate = new Date(prev);
        newDate.setDate(newDate.getDate() + 7);
        return newDate;
    });
    
    const goToToday = () => setCurrentWeekStartDate(getWeekStartDate(new Date(), startDayIndex));

    const handleUpdateSchedule = useCallback((staffId: string, date: string, shiftId: string | null, status: AssignmentStatus, isForced?: boolean) => {
        onCellUpdate(staffId, date, shiftId, status, isForced);
    }, [onCellUpdate]);

    const handleRepeat = (repeatStartDate: Date, repeatEndDate: Date) => {
        onRepeatSchedule(currentWeekStartDate, repeatStartDate, repeatEndDate);
        setIsRepeatModalOpen(false);
    };

    const handlePrint = () => {
        setFilterActionType('print');
        if (selectedDeptIds.length === 0) {
            setSelectedDeptIds(departments.map(d => d.id));
        }
        setIsFilterModalOpen(true);
    };

    const handleDownloadPDF = () => {
        setFilterActionType('pdf');
        if (selectedDeptIds.length === 0) {
            setSelectedDeptIds(departments.map(d => d.id));
        }
        setIsFilterModalOpen(true);
    };

    const executePDFDownload = () => {
        console.log("PDF Generation Triggered (Scheduler): Initiating custom pre-processing sequence.");
        const element = document.getElementById('printable-pdf-content');
        if (!element) {
            console.error("PDF Generation Aborted (Scheduler): Target element '#printable-pdf-content' was not found in the DOM.");
            toast.error("Error finding schedule content to compile.");
            setPdfFilterDeptIds(null);
            return;
        }
        setIsDownloadingPDF(true);

        // Sanitize active stylesheets containing oklch colors prior to conversion
        console.log("PDF Generation Step 1 (Scheduler): Temporarily sanitizing OKLCH and OKLAB stylesheet declarations.");
        const restoreStylesheets = sanitizeStylesheetsForPDF();
        
        console.log("PDF Generation Step 2 (Scheduler): Recursively injecting converted RGB/RGBA inline styles on container DOM branches.");
        const restoreInline = applyRGBInlineStyles(element);

        const filename = `schedule-${formatDateRange(currentWeekStartDate).replace(/\s+/g, '_')}.pdf`;
        console.log(`PDF Generation Step 3 (Scheduler): Configured destination filename correctly: "${filename}"`);

        // Prevent layout shift/clipping by temporarily overriding max-width and scroll constraints during generation
        const originalMaxWidth = element.style.maxWidth;
        const originalOverflowX = element.style.overflowX;
        element.style.maxWidth = 'none';
        element.style.overflowX = 'visible';

        const opt = {
            margin:       0,
            filename:     filename,
            image:        { type: 'jpeg' as const, quality: 0.98 },
            html2canvas:  { 
                scale: 2, 
                useCORS: true, 
                logging: false,
                width: 1056,
                windowWidth: 1056,
                scrollX: 0,
                scrollY: 0,
                x: 0,
                y: 0
            },
            jsPDF:        { unit: 'px' as const, format: [1056, 816] as [number, number], orientation: 'landscape' as const },
            pagebreak:    { mode: ['css', 'legacy'] }
        };

        try {
            console.log("PDF Generation Step 4 (Scheduler): Loading html2pdf rendering library instance.");
            let html2pdfInstance = (window as any).html2pdf || html2pdf;
            if (html2pdfInstance && typeof html2pdfInstance !== 'function' && (html2pdfInstance as any).default) {
                html2pdfInstance = (html2pdfInstance as any).default;
            }

            if (!html2pdfInstance || typeof html2pdfInstance !== 'function') {
                throw new Error("html2pdf library load failure - constructor is not a valid function.");
            }

            const isInIframe = window.self !== window.top;
            console.log(`PDF Generation Step 5 (Scheduler): Rendering context identified. Browser iframe sandbox active: ${isInIframe}`);

            if (!isInIframe) {
                console.log("PDF Generation Step 6A (Scheduler): Not inside sandboxed iframe workspace view. Triggering standard native .save() direct stream.");
                html2pdfInstance()
                    .from(element)
                    .set(opt)
                    .save()
                    .then(() => {
                        console.log("PDF Generation Complete (Scheduler): Standard native download finished successfully.");
                        setIsDownloadingPDF(false);
                        toast.success("PDF schedule downloaded successfully!");
                    })
                    .catch((err: any) => {
                        console.error("PDF download error (Scheduler) during .save() stage:", err);
                        setIsDownloadingPDF(false);
                        toast.error("Failed to generate PDF download.");
                    })
                    .finally(() => {
                        console.log("PDF Generation Step 7A (Scheduler): Restoring original document styles and active classes.");
                        element.style.maxWidth = originalMaxWidth;
                        element.style.overflowX = originalOverflowX;
                        restoreStylesheets();
                        restoreInline();
                        setPdfFilterDeptIds(null);
                    });
            } else {
                console.log("PDF Generation Step 6B (Scheduler): Inside sandbox iframe. Bypassing direct save browser blocks by rendering safely to Blob.");
                html2pdfInstance()
                    .from(element)
                    .set(opt)
                    .output('blob')
                    .then((blob: Blob) => {
                        console.log("PDF Generation Step 7B (Scheduler): successfully converted layout tree to binary container. Creating download reference URL.");
                        const blobUrl = URL.createObjectURL(blob);
                        setPdfBlobUrl(blobUrl);
                        setPdfFilename(filename);
                        setIsDownloadingPDF(false);
                        toast.success("PDF Compiled Successfully!");
                    })
                    .catch((err: any) => {
                        console.error("PDF compiling error (Scheduler) during output('blob') stage:", err);
                        setIsDownloadingPDF(false);
                        toast.error("Could not compile PDF preview.");
                    })
                    .finally(() => {
                        console.log("PDF Generation Step 8B (Scheduler): Restoring original document styles and active classes.");
                        element.style.maxWidth = originalMaxWidth;
                        element.style.overflowX = originalOverflowX;
                        restoreStylesheets();
                        restoreInline();
                        setPdfFilterDeptIds(null);
                    });
            }
        } catch (err: any) {
            console.error("PDF export initialization crash during rendering chain orchestration (Scheduler):", err);
            setIsDownloadingPDF(false);
            toast.error("PDF generation engine could not be loaded. Please try again.");
            element.style.maxWidth = originalMaxWidth;
            element.style.overflowX = originalOverflowX;
            restoreStylesheets();
            restoreInline();
            setPdfFilterDeptIds(null);
        }
    };

    const handleGenerateFilteredReport = () => {
        if (selectedDeptIds.length === 0) {
            toast.error('Please select at least one department.');
            return;
        }

        if (filterActionType === 'print') {
            setIsFilterModalOpen(false);
            const filteredDepts = departments.filter(d => selectedDeptIds.includes(d.id));
            const filteredPeople = people.filter(p => !p.departmentId || selectedDeptIds.includes(p.departmentId));
            onInitiatePrint({
                departments: filteredDepts,
                people: filteredPeople,
                shifts,
                schedule,
                weekStartDate: currentWeekStartDate,
                weeklyMetrics,
            });
        } else if (filterActionType === 'pdf') {
            setIsFilterModalOpen(false);
            const filteredDepts = departments.filter(d => selectedDeptIds.includes(d.id));
            const filteredPeople = people.filter(p => !p.departmentId || selectedDeptIds.includes(p.departmentId));
            if (onInitiatePDFPreview) {
                onInitiatePDFPreview({
                    departments: filteredDepts,
                    people: filteredPeople,
                    shifts,
                    schedule,
                    weekStartDate: currentWeekStartDate,
                    weeklyMetrics,
                });
            } else {
                setPdfFilterDeptIds(selectedDeptIds);
                setIsPendingPDFGeneration(true);
            }
        }
    };

    React.useEffect(() => {
        if (isPendingPDFGeneration && pdfFilterDeptIds) {
            setIsPendingPDFGeneration(false);
            const timer = setTimeout(() => {
                executePDFDownload();
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [isPendingPDFGeneration, pdfFilterDeptIds]);

    const departmentStaffMap = useMemo(() => {
        const map = new Map<string, SchedulablePerson[]>();
        departments.forEach(dep => map.set(dep.id, []));
        people.forEach(person => {
            if (person.departmentId && map.has(person.departmentId)) {
                map.get(person.departmentId)?.push(person);
            }
        });
        return map;
    }, [departments, people]);

    const printableRows = useMemo(() => {
        const rows: {
            type: 'dept' | 'staff';
            department: Department;
            person?: SchedulablePerson;
        }[] = [];

        activePdfDepts.forEach(dep => {
            const depStaff = departmentStaffMap.get(dep.id) || [];
            if (depStaff.length === 0) return;

            rows.push({
                type: 'dept',
                department: dep
            });

            depStaff.forEach(person => {
                rows.push({
                    type: 'staff',
                    department: dep,
                    person
                });
            });
        });

        return rows;
    }, [activePdfDepts, departmentStaffMap]);

    const pdfPagesData = useMemo(() => {
        const activePeople = people.filter(p => !p.departmentId || activePdfDepts.some(d => d.id === p.departmentId));
        const totalStaff = activePeople.length;
        const isLargeSchedule = totalStaff > 15;
        const isVeryLargeSchedule = totalStaff > 30;

        // Estimated height of standard rows in px (including flexible heights + vertical padding)
        const baseRowHeight = isVeryLargeSchedule ? 30 : isLargeSchedule ? 38 : 46;
        const maxPageHeight = 580; // Safety threshold for page height budgeting

        const pages: (typeof printableRows)[] = [];
        let currentTempPage: typeof printableRows = [];
        let currentPageHeight = 0;

        printableRows.forEach(row => {
            const estRowHeight = row.type === 'dept' ? baseRowHeight * 0.8 : baseRowHeight;
            if (currentTempPage.length > 0 && currentPageHeight + estRowHeight > maxPageHeight) {
                pages.push(currentTempPage);
                currentTempPage = [];
                currentPageHeight = 0;
            }
            currentTempPage.push(row);
            currentPageHeight += estRowHeight;
        });

        if (currentTempPage.length > 0) {
            pages.push(currentTempPage);
        }

        if (pages.length === 0) {
            pages.push([]);
        }

        return pages;
    }, [printableRows, people, activePdfDepts]);

    const dailyStaffCount = useMemo(() => {
        const counts: { [date: string]: { [depId: string]: number } } = {};
        
        weekDates.forEach(date => {
            const dateString = formatDateYYYYMMDD(date);
            counts[dateString] = {};
            departments.forEach(dep => {
                counts[dateString][dep.id] = 0;
            });
        });

        people.forEach(person => {
            const personSchedule = schedule[person.id];
            if (personSchedule && person.departmentId) {
                const depId = person.departmentId;
                weekDates.forEach(date => {
                    const dateString = formatDateYYYYMMDD(date);
                    const entry = personSchedule[dateString];
                    if (entry && entry.status === 'ASSIGNED') {
                        const dateCounts = counts[dateString];
                        if (dateCounts && typeof dateCounts[depId] === 'number') {
                            dateCounts[depId]++;
                        }
                    }
                });
            }
        });

        return counts;
    }, [schedule, people, departments, weekDates]);

    const dailyDepartmentHours = useMemo(() => {
        const hours: { [date: string]: { [depId: string]: number } } = {};
        const shiftsMap = new Map<string, Shift>(shifts.map(s => [s.id, s]));
        
        weekDates.forEach(date => {
            const dateString = formatDateYYYYMMDD(date);
            hours[dateString] = {};
            departments.forEach(dep => {
                hours[dateString][dep.id] = 0;
            });
        });

        people.forEach(person => {
            const personSchedule = schedule[person.id];
            if (personSchedule && person.departmentId) {
                const depId = person.departmentId;
                weekDates.forEach(date => {
                    const dateString = formatDateYYYYMMDD(date);
                    const entry = personSchedule[dateString];
                    if (entry && entry.status === 'ASSIGNED' && entry.shiftId) {
                        const shift = shiftsMap.get(entry.shiftId);
                        if (shift) {
                            const start = new Date(`1970-01-01T${shift.startTime}:00`);
                            const end = new Date(`1970-01-01T${shift.endTime}:00`);
                            if (end < start) { // Handles overnight shifts
                                end.setDate(end.getDate() + 1);
                            }
                            const shiftHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                            const dateHours = hours[dateString];
                            if (dateHours && typeof dateHours[depId] === 'number') {
                                dateHours[depId] += shiftHours;
                            }
                        }
                    }
                });
            }
        });

        return hours;
    }, [schedule, people, departments, weekDates, shifts]);

    const handleMetricChange = (dateString: string, field: keyof WeeklyMetrics[string], value: string) => {
        const numValue = Math.max(0, parseInt(value) || 0);
        setWeeklyMetrics(prev => ({
            ...prev,
            [dateString]: {
                ...(prev[dateString] || { arrivals: 0, departures: 0, stayOvers: 0, occupancy: 0 }),
                [field]: numValue
            }
        }));
    };

    const staffWeeklyHours = useMemo(() => {
        const hours: { [staffId: string]: number } = {};
        const shiftsMap = new Map<string, Shift>(shifts.map(s => [s.id, s]));
        for (const person of people) {
            let personHours = 0;
            for (const date of weekDates) {
                const dateString = formatDateYYYYMMDD(date);
                const entry = schedule[person.id]?.[dateString];
                if (entry?.status === 'ASSIGNED' && entry.shiftId) {
                    const shift = shiftsMap.get(entry.shiftId);
                    if (shift) {
                        const start = new Date(`1970-01-01T${shift.startTime}:00`);
                        const end = new Date(`1970-01-01T${shift.endTime}:00`);
                        if (end < start) { // Handles overnight shifts
                            end.setDate(end.getDate() + 1);
                        }
                        personHours += (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                    }
                }
            }
            hours[person.id] = personHours;
        }
        return hours;
    }, [schedule, people, shifts, weekDates]);

    const totalWeeklyHours = useMemo(() => {
        // FIX: Explicitly type the parameters of the 'reduce' function to resolve a TypeScript inference issue where the '+' operator was being applied to 'unknown' types. This ensures 'sum' and 'hours' are correctly treated as numbers.
        return Object.values(staffWeeklyHours).reduce((sum: number, hours: number) => sum + hours, 0);
    }, [staffWeeklyHours]);

    const departmentWeeklyHours = useMemo(() => {
        const hours: { [depId: string]: number } = {};
        departments.forEach(dep => {
            hours[dep.id] = 0;
        });

        people.forEach(person => {
            if (person.departmentId && hours[person.departmentId] !== undefined) {
                hours[person.departmentId] += staffWeeklyHours[person.id] || 0;
            }
        });

        return hours;
    }, [departments, people, staffWeeklyHours]);

    const selectedStaffDepartment = useMemo(() => {
        if (!selectedStaffForProfile?.departmentId) return undefined;
        return departments.find(d => d.id === selectedStaffForProfile.departmentId);
    }, [selectedStaffForProfile, departments]);


    return (
        <div className="flex flex-col lg:flex-row gap-6">
             {isRepeatModalOpen && (
                <RepeatScheduleModal
                    sourceWeekStart={currentWeekStartDate}
                    onClose={() => setIsRepeatModalOpen(false)}
                    onRepeat={handleRepeat}
                />
             )}
             {selectedStaffForProfile && (
                <StaffProfileModal
                    staff={selectedStaffForProfile as StaffMember}
                    department={selectedStaffDepartment}
                    onClose={() => setSelectedStaffForProfile(null)}
                />
             )}
             {isImportModalOpen && (
                <CSVImportModal
                    onClose={() => setIsImportModalOpen(false)}
                    people={people}
                    schedule={schedule}
                    weeklyMetrics={weeklyMetrics}
                    setSchedule={setSchedule}
                    setWeeklyMetrics={setWeeklyMetrics}
                    weekStartDay={weekStartDay}
                    currentWeekStartDate={currentWeekStartDate}
                    setCurrentWeekStartDate={setCurrentWeekStartDate}
                />
             )}
             {isFilterModalOpen && (
                <Modal 
                    title={filterActionType === 'print' ? 'Print Schedule Report' : 'Download PDF Report'} 
                    onClose={() => setIsFilterModalOpen(false)}
                >
                    <div className="p-6 space-y-4">
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                            Select one or more departments to include in the generated report. Only staff members and data from these departments will be shown.
                        </p>

                        <div className="flex items-center justify-between border-b pb-2 mb-2 dark:border-slate-700">
                            <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Departments ({selectedDeptIds.length} of {departments.length} selected)
                            </span>
                            <div className="space-x-2">
                                <button
                                    type="button"
                                    onClick={() => setSelectedDeptIds(departments.map(d => d.id))}
                                    className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
                                >
                                    Select All
                                </button>
                                <span className="text-gray-300 dark:text-slate-700">|</span>
                                <button
                                    type="button"
                                    onClick={() => setSelectedDeptIds([])}
                                    className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
                                >
                                    Deselect All
                                </button>
                            </div>
                        </div>

                        <div className="max-h-60 overflow-y-auto space-y-2.5 pr-2">
                            {departments.map(dep => {
                                const isChecked = selectedDeptIds.includes(dep.id);
                                return (
                                    <label 
                                        key={dep.id} 
                                        className="flex items-center gap-3 p-2.5 hover:bg-gray-50 dark:hover:bg-slate-700/50 rounded-lg cursor-pointer transition border border-transparent hover:border-gray-150 dark:hover:border-slate-700/60"
                                        style={{ borderLeft: `3px solid ${dep.color}` }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => {
                                                if (isChecked) {
                                                    setSelectedDeptIds(prev => prev.filter(id => id !== dep.id));
                                                } else {
                                                    setSelectedDeptIds(prev => [...prev, dep.id]);
                                                }
                                            }}
                                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-700"
                                        />
                                        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                            {dep.name}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-slate-900/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t dark:border-slate-700">
                        <button
                            type="button"
                            onClick={handleGenerateFilteredReport}
                            disabled={selectedDeptIds.length === 0}
                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed transition"
                        >
                            {filterActionType === 'print' ? 'Print Schedule' : 'Download PDF'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsFilterModalOpen(false)}
                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-slate-600 shadow-sm px-4 py-2 bg-white dark:bg-slate-700 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                        >
                            Cancel
                        </button>
                    </div>
                </Modal>
              )}
            <div className={`flex-shrink-0 transition-all duration-300 ease-in-out ${isPaletteCollapsed ? 'lg:w-20' : 'lg:w-1/4 xl:w-1/5'}`}>
                <ShiftPalette 
                    shifts={shifts} 
                    setDraggedShiftId={setDraggedShiftId} 
                    isCollapsed={isPaletteCollapsed}
                    onToggleCollapse={() => setIsPaletteCollapsed(prev => !prev)}
                />
            </div>

            <div className="flex-1 overflow-x-auto">
                 <WeekNavigator 
                    currentWeekStartDate={currentWeekStartDate}
                    onPrevious={goToPreviousWeek}
                    onNext={goToNextWeek}
                    onToday={goToToday}
                    onRepeat={() => setIsRepeatModalOpen(true)}
                    onPrint={handlePrint}
                    onDownloadPDF={handleDownloadPDF}
                    onImportCSV={() => setIsImportModalOpen(true)}
                    totalWeeklyHours={totalWeeklyHours}
                    isDownloadingPDF={isDownloadingPDF}
                />

                {/* Attendance Metrics Summary Action Card */}
                <div className="flex items-center justify-between mb-4 px-4 py-2.5 bg-slate-50 dark:bg-slate-800/20 border border-gray-200 dark:border-slate-700/65 rounded-xl shadow-sm">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-teal-50 dark:bg-teal-950/40 border border-teal-100 dark:border-teal-900 rounded-lg text-teal-600 dark:text-teal-400">
                            <Icons.BarChart className="w-4 h-4" />
                        </div>
                        <div>
                            <h4 className="text-xs font-bold text-gray-800 dark:text-gray-105">Attendance Metrics Summary</h4>
                            <p className="text-[10px] text-gray-500 dark:text-gray-400">Review real-time occupancy percentages, stay-overs, arrivals, and departures.</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => setIsImportModalOpen(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-white hover:bg-gray-50 dark:bg-slate-800 dark:hover:bg-slate-750 text-teal-600 dark:text-teal-400 border border-gray-300 dark:border-slate-600 rounded-lg shadow-sm transition"
                        id="attendance-metrics-import-btn"
                    >
                        <svg className="w-3.5 h-3.5 text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Import CSV
                    </button>
                </div>
                <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-md min-w-[1200px]">
                    <div className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-y-auto max-h-[calc(100vh-200px)]">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                            <thead className="bg-gray-50 dark:bg-slate-900/50 sticky top-0 z-30 shadow-sm">
                                <tr className="z-30">
                                    <th scope="col" className="sticky left-0 bg-gray-50 dark:bg-slate-900/50 z-40 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-48">
                                        Staff Member
                                    </th>
                                    {weekDates.map((date, index) => {
                                        const { day, date: dateNum } = formatDayAndDate(date);
                                        return (
                                            <th key={date.toISOString()} scope="col" className={`px-3 py-2 text-center w-36 ${dayHeaderStyles[index]} ${dayHeaderBorderStyles[index]} bg-gray-50 dark:bg-slate-900/50 z-30`}>
                                                <div className="text-2xl font-bold">{dateNum}</div>
                                                <div className="text-xs font-semibold uppercase tracking-wider">{day}</div>
                                            </th>
                                        )
                                    })}
                                    <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24 bg-gray-50 dark:bg-slate-900/50 z-30">
                                        Hours
                                    </th>
                                </tr>
                                {/* Arrivals Metric Row */}
                                <tr className="bg-emerald-50/10 dark:bg-emerald-900/10 border-b border-emerald-100/50 dark:border-emerald-900/20 z-20">
                                    <th className="sticky left-0 bg-emerald-50/20 dark:bg-emerald-900/20 backdrop-blur-sm z-40 px-4 py-1.5 text-left text-[10px] font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider w-48 border-r border-emerald-100 dark:border-emerald-900/30">
                                        Arrivals
                                    </th>
                                    {weekDates.map(date => {
                                        const ds = formatDateYYYYMMDD(date);
                                        const val = weeklyMetrics[ds]?.arrivals || 0;
                                        return (
                                            <td key={`${ds}-arrivals`} className="px-3 py-1 text-center border-r border-emerald-100/30 dark:border-emerald-900/10">
                                                <div className="flex items-center justify-center">
                                                    <input
                                                        type="number"
                                                        value={val || ''}
                                                        placeholder="0"
                                                        onChange={(e) => handleMetricChange(ds, 'arrivals', e.target.value)}
                                                        className="w-12 text-[11px] text-center border border-emerald-200 dark:border-emerald-800 rounded bg-white dark:bg-slate-800 text-emerald-900 dark:text-emerald-100 focus:ring-1 focus:ring-emerald-500 outline-none py-0.5"
                                                    />
                                                </div>
                                            </td>
                                        );
                                    })}
                                    <td className="bg-emerald-50/10 dark:bg-emerald-900/10 border-emerald-100 dark:border-emerald-900/30"></td>
                                </tr>
                                {/* Departures Metric Row */}
                                <tr className="bg-rose-50/10 dark:bg-rose-900/10 border-b border-rose-100/50 dark:border-rose-900/20 z-20">
                                    <th className="sticky left-0 bg-rose-50/20 dark:bg-rose-900/20 backdrop-blur-sm z-40 px-4 py-1.5 text-left text-[10px] font-bold text-rose-700 dark:text-rose-400 uppercase tracking-wider w-48 border-r border-rose-100 dark:border-rose-900/30">
                                        Departures
                                    </th>
                                    {weekDates.map(date => {
                                        const ds = formatDateYYYYMMDD(date);
                                        const val = weeklyMetrics[ds]?.departures || 0;
                                        return (
                                            <td key={`${ds}-departures`} className="px-3 py-1 text-center border-r border-rose-100/30 dark:border-rose-900/10">
                                                <div className="flex items-center justify-center">
                                                    <input
                                                        type="number"
                                                        value={val || ''}
                                                        placeholder="0"
                                                        onChange={(e) => handleMetricChange(ds, 'departures', e.target.value)}
                                                        className="w-12 text-[11px] text-center border border-rose-200 dark:border-rose-800 rounded bg-white dark:bg-slate-800 text-rose-900 dark:text-rose-100 focus:ring-1 focus:ring-rose-500 outline-none py-0.5"
                                                    />
                                                </div>
                                            </td>
                                        );
                                    })}
                                    <td className="bg-rose-50/10 dark:bg-rose-900/10 border-rose-100 dark:border-rose-900/30"></td>
                                </tr>
                                {/* Stay-overs Metric Row */}
                                <tr className="bg-blue-50/10 dark:bg-blue-900/10 border-b border-blue-100/50 dark:border-blue-900/20 z-20">
                                    <th className="sticky left-0 bg-blue-50/20 dark:bg-blue-900/20 backdrop-blur-sm z-40 px-4 py-1.5 text-left text-[10px] font-bold text-blue-700 dark:text-blue-400 uppercase tracking-wider w-48 border-r border-blue-100 dark:border-blue-900/30">
                                        Stay-overs
                                    </th>
                                    {weekDates.map(date => {
                                        const ds = formatDateYYYYMMDD(date);
                                        const val = weeklyMetrics[ds]?.stayOvers || 0;
                                        return (
                                            <td key={`${ds}-stayOvers`} className="px-3 py-1 text-center border-r border-blue-100/30 dark:border-blue-900/10">
                                                <div className="flex items-center justify-center">
                                                    <input
                                                        type="number"
                                                        value={val || ''}
                                                        placeholder="0"
                                                        onChange={(e) => handleMetricChange(ds, 'stayOvers', e.target.value)}
                                                        className="w-12 text-[11px] text-center border border-blue-200 dark:border-blue-800 rounded bg-white dark:bg-slate-800 text-blue-900 dark:text-blue-100 focus:ring-1 focus:ring-blue-500 outline-none py-0.5"
                                                    />
                                                </div>
                                            </td>
                                        );
                                    })}
                                    <td className="bg-blue-50/10 dark:bg-blue-900/10 border-blue-100 dark:border-blue-900/30"></td>
                                </tr>
                                {/* Occupancy Metric Row */}
                                <tr className="bg-amber-50/10 dark:bg-amber-900/10 border-b border-amber-100/50 dark:border-amber-900/20 z-20">
                                    <th className="sticky left-0 bg-amber-50/20 dark:bg-amber-900/20 backdrop-blur-sm z-40 px-4 py-1.5 text-left text-[10px] font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wider w-48 border-r border-amber-100 dark:border-amber-900/30">
                                        Occupancy
                                    </th>
                                    {weekDates.map(date => {
                                        const ds = formatDateYYYYMMDD(date);
                                        const val = weeklyMetrics[ds]?.occupancy || 0;
                                        return (
                                            <td key={`${ds}-occupancy`} className="px-3 py-1 text-center border-r border-amber-100/30 dark:border-amber-900/10">
                                                <div className="flex items-center justify-center gap-1">
                                                    <input
                                                        type="number"
                                                        value={val || ''}
                                                        placeholder="0"
                                                        onChange={(e) => handleMetricChange(ds, 'occupancy', e.target.value)}
                                                        className="w-12 text-[11px] text-center border border-amber-200 dark:border-amber-800 rounded bg-white dark:bg-slate-800 text-amber-900 dark:text-amber-100 focus:ring-1 focus:ring-amber-500 outline-none py-0.5"
                                                    />
                                                    <span className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 select-none">%</span>
                                                </div>
                                            </td>
                                        );
                                    })}
                                    <td className="bg-amber-50/10 dark:bg-amber-900/10 border-amber-100 dark:border-amber-900/30"></td>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
                                {departments.map(dep => (
                                    <React.Fragment key={dep.id}>
                                        <tr className="bg-gray-200 dark:bg-slate-700 sticky top-[57px] z-20 shadow-sm">
                                            <td className="sticky left-0 bg-gray-200 dark:bg-slate-700 z-30 px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 w-48 border-r border-gray-300 dark:border-slate-600" style={{ color: dep.color }}>
                                                {dep.name}
                                            </td>
                                            {weekDates.map(date => {
                                                const dateString = formatDateYYYYMMDD(date);
                                                return (
                                                    <td key={dateString} className="px-3 py-1 text-center text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-200 dark:bg-slate-700 z-20">
                                                        <div className="leading-tight">{dailyStaffCount[dateString]?.[dep.id] || 0} Staff</div>
                                                        <div className="text-[10px] font-medium text-gray-500 dark:text-gray-400 leading-tight">
                                                            {(dailyDepartmentHours[dateString]?.[dep.id] || 0).toFixed(1)} hrs
                                                        </div>
                                                    </td>
                                                )
                                            })}
                                            <td className="px-4 py-2 text-center text-sm font-bold text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-slate-700 z-20">
                                                {departmentWeeklyHours[dep.id]?.toFixed(1)}
                                            </td>
                                        </tr>
                                        {departmentStaffMap.get(dep.id)?.map(person => (
                                            <tr key={person.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                                                <td className="sticky left-0 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700/50 z-10 px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100 w-48" style={{ borderLeft: `4px solid ${person.color}` }}>
                                                    <div className="flex items-center justify-between">
                                                        <div className="truncate">
                                                            {person.name}
                                                            <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2 mt-0.5">
                                                                <span className="truncate max-w-[100px]">{person.role}</span>
                                                                <button 
                                                                    onClick={() => setSelectedStaffForProfile(person)}
                                                                    className="p-1 rounded-md bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-all shadow-sm border border-indigo-100 dark:border-indigo-800/50"
                                                                    title="View Staff Profile"
                                                                >
                                                                    <Icons.ExternalLink className="w-3.5 h-3.5" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                {weekDates.map(date => {
                                                    const dateString = formatDateYYYYMMDD(date);
                                                    return (
                                                        <ScheduleCell
                                                            key={`${person.id}-${dateString}`}
                                                            staffId={person.id}
                                                            date={dateString}
                                                            entry={schedule[person.id]?.[dateString]}
                                                            shifts={shifts}
                                                            onUpdate={handleUpdateSchedule}
                                                            draggedShiftId={draggedShiftId}
                                                            personName={person.name}
                                                            personAvailability={{
                                                                availability: person.availability,
                                                                availabilityOverrides: person.availabilityOverrides,
                                                            }}
                                                        />
                                                    )
                                                })}
                                                <td className="px-4 py-2 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                                                    {staffWeeklyHours[person.id]?.toFixed(1)}
                                                </td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Hidden Landscape PDF Container for html2pdf conversion */}
            <div style={{ height: 0, overflow: 'hidden', pointerEvents: 'none', position: 'relative' }}>
                <div id="printable-pdf-content" style={{ width: '1056px', backgroundColor: '#ffffff', color: '#1e293b', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                    {pdfPagesData.map((pageRows, pageIndex) => {
                        const pageNum = pageIndex + 1;
                        const totalPages = pdfPagesData.length;

                        const totalStaff = people.length;
                        const isLargeSchedule = totalStaff > 15;
                        const isVeryLargeSchedule = totalStaff > 30;

                        // Unified, dynamic cell styling to ensure consistent auto-resize and top-alignment without vertical clipping
                        const cellStyle: React.CSSProperties = {
                            paddingTop: isVeryLargeSchedule ? '4px' : isLargeSchedule ? '6px' : '8px',
                            paddingBottom: isVeryLargeSchedule ? '4px' : isLargeSchedule ? '6px' : '8px',
                            paddingLeft: isVeryLargeSchedule ? '4px' : isLargeSchedule ? '6px' : '8px',
                            paddingRight: isVeryLargeSchedule ? '4px' : isLargeSchedule ? '6px' : '8px',
                            lineHeight: isVeryLargeSchedule ? '1.2' : isLargeSchedule ? '1.3' : '1.4',
                            verticalAlign: 'top',
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                            border: '1px solid #cbd5e1',
                            fontSize: isVeryLargeSchedule ? '8px' : isLargeSchedule ? '9px' : '10px'
                        };

                        const headerCellStyle: React.CSSProperties = {
                            ...cellStyle,
                            backgroundColor: '#f1f5f9',
                            color: '#334155',
                            fontWeight: 'bold'
                        };

                        return (
                            <React.Fragment key={`pdf-page-${pageIndex}`}>
                                <div 
                                    className="pdf-page bg-white" 
                                    style={{ 
                                        width: '1056px', 
                                        height: '816px', 
                                        padding: '38px 38px 38px 60px', 
                                        boxSizing: 'border-box', 
                                        position: 'relative', 
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        justifyContent: 'space-between',
                                        backgroundColor: '#ffffff'
                                    }}
                                >
                                    <div>
                                        {/* PDF Header Block (Title, Date Range, Generated Date ONLY - no metrics, stats, or totals) */}
                                        <div className="flex items-center justify-between pb-2 mb-3" style={{ borderBottom: '1px solid #cbd5e1' }}>
                                            <div>
                                                <h1 className="text-sm font-black tracking-tight uppercase" style={{ color: '#0f172a' }}>Weekly Schedule Report</h1>
                                                <p className="text-[8px] font-bold" style={{ color: '#64748b' }}>
                                                    Scheduler Pro Platform {totalPages > 1 && `| Page ${pageNum} of ${totalPages}`}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <h2 className="text-xs font-bold" style={{ color: '#1e293b' }}>{formatDateRange(currentWeekStartDate)}</h2>
                                                <p className="text-[8px] font-semibold mt-0.5" style={{ color: '#64748b' }}>
                                                    Generated On: <span className="font-bold">{new Date().toLocaleDateString()} {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                                </p>
                                            </div>
                                        </div>

                                        {/* PDF Table Grid with table-layout: auto for dynamic row-height / auto-wrap */}
                                        <table className="w-full border-collapse" style={{ border: '1px solid #cbd5e1', tableLayout: 'auto', width: '958px' }}>
                                            <thead>
                                                <tr style={{ backgroundColor: '#f1f5f9', color: '#334155' }}>
                                                    <th style={{ ...headerCellStyle, width: '16%' }} className="text-left uppercase tracking-wider">
                                                        Staff Member
                                                    </th>
                                                    {weekDates.map((date) => {
                                                        const { day, date: dateNum } = formatDayAndDate(date);
                                                        return (
                                                            <th key={`${date.toISOString()}-pdf-hdr-${pageIndex}`} style={{ ...headerCellStyle, backgroundColor: '#f8fafc', width: '11%' }} className="text-center">
                                                                <div className="font-bold text-[10px]" style={{ color: '#1e293b' }}>{dateNum}</div>
                                                                <div className="text-[8px] font-bold uppercase tracking-wide" style={{ color: '#64748b' }}>{day}</div>
                                                            </th>
                                                        );
                                                    })}
                                                    <th style={{ ...headerCellStyle, backgroundColor: '#f8fafc', width: '7%' }} className="text-center uppercase tracking-wider">
                                                        Hours
                                                    </th>
                                                </tr>
                                                <tr style={{ backgroundColor: '#fffbeb', color: '#b45309' }}>
                                                    <td style={{ ...cellStyle, fontWeight: 'bold', backgroundColor: '#fef3c7', color: '#92400e', borderBottom: '1.5px solid #cbd5e1' }} className="text-left text-[10px] font-bold uppercase tracking-wider">
                                                        Occupancy
                                                    </td>
                                                    {weekDates.map((date) => {
                                                        const dateString = formatDateYYYYMMDD(date);
                                                        const occ = weeklyMetrics[dateString]?.occupancy || 0;
                                                        return (
                                                            <td key={`${date.toISOString()}-pdf-hdr-occ-${pageIndex}`} style={{ ...cellStyle, backgroundColor: '#fffbeb', fontWeight: 'bold', color: '#b45309', borderBottom: '1.5px solid #cbd5e1' }} className="text-center text-[10px]">
                                                                {occ}%
                                                            </td>
                                                        );
                                                    })}
                                                    <td style={{ ...cellStyle, backgroundColor: '#fef3c7', borderBottom: '1.5px solid #cbd5e1' }} className="text-center text-[10px]"></td>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {pageRows.map((row, rowIndex) => {
                                                    if (row.type === 'dept') {
                                                        return (
                                                            <tr key={`pdf-page-${pageIndex}-dept-${rowIndex}`} style={{ backgroundColor: '#f1f5f9', color: '#334155', fontWeight: 'bold' }}>
                                                                <td style={{ ...cellStyle, borderLeft: `3px solid ${row.department.color}`, fontWeight: 'bold' }}>
                                                                    <span style={{ color: row.department.color }}>{row.department.name}</span>
                                                                </td>
                                                                {weekDates.map(date => {
                                                                    const dateString = formatDateYYYYMMDD(date);
                                                                    return (
                                                                        <td key={`${dateString}-pdf-dep-tot-${pageIndex}-${rowIndex}`} style={cellStyle} className="text-center">
                                                                            <div className="font-semibold leading-none">{dailyStaffCount[dateString]?.[row.department.id] || 0} Staff</div>
                                                                            <div className="text-[7px] mt-0.5" style={{ color: '#94a3b8' }}>{(dailyDepartmentHours[dateString]?.[row.department.id] || 0).toFixed(1)}h</div>
                                                                        </td>
                                                                    );
                                                                })}
                                                                <td style={{ ...cellStyle, backgroundColor: '#f1f5f9', fontWeight: 'bold', color: '#334155' }} className="text-center">
                                                                    {departmentWeeklyHours[row.department.id]?.toFixed(1)}
                                                                </td>
                                                            </tr>
                                                        );
                                                    } else if (row.type === 'staff' && row.person) {
                                                        const person = row.person;
                                                        return (
                                                            <tr key={`pdf-page-${pageIndex}-staff-${person.id}`} style={{ backgroundColor: '#ffffff' }}>
                                                                <td style={{ ...cellStyle, borderLeft: `3px solid ${person.color || row.department.color}`, fontWeight: 'bold', color: '#1e293b' }}>
                                                                    <div className="font-bold text-gray-950">{person.name}</div>
                                                                    <div className="text-[8px] font-normal leading-tight mt-1" style={{ color: '#64748b' }}>{person.role}</div>
                                                                </td>
                                                                {weekDates.map(date => {
                                                                    const dateString = formatDateYYYYMMDD(date);
                                                                    const entry = schedule[person.id]?.[dateString];
                                                                    let content: React.ReactNode = <span style={{ color: '#cbd5e1' }}>-</span>;
                                                                    let bgColor = 'transparent';
                                                                    let borderLeftVal = 'none';

                                                                    if (entry) {
                                                                         if (entry.status === 'ASSIGNED' && entry.shiftId) {
                                                                             const shift = shifts.find(s => s.id === entry.shiftId);
                                                                             if (shift) {
                                                                                 content = (
                                                                                     <div className="leading-tight">
                                                                                         <div className="font-bold text-[9px] text-gray-900">{shift.name}</div>
                                                                                         <div className="text-[8px] font-medium mt-1 text-gray-500">{shift.startTime}-{shift.endTime}</div>
                                                                                     </div>
                                                                                 );
                                                                                 const isHex = shift.color?.startsWith('#');
                                                                                 bgColor = isHex ? `${shift.color}15` : 'rgba(99, 102, 241, 0.08)';
                                                                                 borderLeftVal = `2px solid ${shift.color || '#cbd5e1'}`;
                                                                             }
                                                                         } else {
                                                                              const labels: { [key: string]: string } = {
                                                                                  OFF: 'OFF',
                                                                                  SICK: 'SICK',
                                                                                  VACATION: 'VAC',
                                                                                  ON_CALL: 'CALL'
                                                                              };
                                                                              content = <span className="font-bold text-[8px] tracking-wider" style={{ color: '#475569' }}>{labels[entry.status] || '-'}</span>;
                                                                              if (entry.status === 'OFF') {
                                                                                  bgColor = '#f3f4f6';
                                                                              } else if (entry.status === 'VACATION') {
                                                                                  bgColor = '#dbeafe';
                                                                              } else if (entry.status === 'SICK') {
                                                                                  bgColor = '#fef9c3';
                                                                              } else if (entry.status === 'ON_CALL') {
                                                                                  bgColor = '#e0f2fe';
                                                                              }
                                                                         }
                                                                    }

                                                                    return (
                                                                        <td
                                                                            key={`${dateString}-pdf-cell-${pageIndex}`}
                                                                            style={{ ...cellStyle, backgroundColor: bgColor, borderLeft: borderLeftVal }}
                                                                            className="text-center align-top"
                                                                        >
                                                                            {content}
                                                                        </td>
                                                                    );
                                                                })}
                                                                <td style={{ ...cellStyle, color: '#334155', backgroundColor: '#f8fafc', fontWeight: 'bold' }} className="text-center">
                                                                    {staffWeeklyHours[person.id]?.toFixed(1)}
                                                                </td>
                                                            </tr>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* PDF Footer Disclaimer */}
                                    <div className="flex justify-between items-center text-[7px] font-semibold pt-2 uppercase tracking-widest" style={{ borderTop: '1px solid #e2e8f0', color: '#94a3b8' }}>
                                        <div>Generated via Scheduler Pro platform</div>
                                        <div>Page {pageNum} of {totalPages}</div>
                                        <div>Date Signed: {new Date().toLocaleDateString()}</div>
                                    </div>
                                </div>
                                {pageNum < totalPages && <div className="html2pdf__page-break" />}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default SchedulerView;